/**
 * KeyboardSequence
 * A reusable utility for detecting keyboard sequences (like cheat codes or shortcuts).
 *
 * @example
 * ```typescript
 * const keySeq = new KeyboardSequence({
 *   sequences: [
 *     {
 *       sequence: '99999',
 *       callback: () => {
 *         localStorage.clear();
 *         window.location.reload();
 *       }
 *     },
 *     {
 *       sequence: 'debug',
 *       callback: () => console.log('Debug mode activated!')
 *     }
 *   ],
 *   caseSensitive: false, // default: false
 *   resetOnNonMatch: true, // default: true
 *   debug: false // default: false
 * });
 *
 * // Clean up when component unmounts
 * keySeq.destroy();
 * ```
 */

interface SequenceConfig {
  sequence: string;
  callback: () => void;
  description?: string;
}

interface KeyboardSequenceOptions {
  sequences: SequenceConfig[];
  caseSensitive?: boolean;
  resetOnNonMatch?: boolean;
  debug?: boolean;
}

export class KeyboardSequence {
  private sequences: SequenceConfig[];
  private buffer: string = '';
  private caseSensitive: boolean;
  private resetOnNonMatch: boolean;
  private debug: boolean;
  private maxLength: number;
  private handleKeyDown: (e: KeyboardEvent) => void;

  constructor(options: KeyboardSequenceOptions) {
    this.sequences = options.sequences;
    this.caseSensitive = options.caseSensitive ?? false;
    this.resetOnNonMatch = options.resetOnNonMatch ?? true;
    this.debug = options.debug ?? false;

    // Find the longest sequence to know how much to buffer
    this.maxLength = Math.max(...this.sequences.map(s => s.sequence.length));

    // Bind the handler
    this.handleKeyDown = this.onKeyDown.bind(this);

    // Start listening
    this.init();
  }

  private init(): void {
    window.addEventListener('keydown', this.handleKeyDown);
    if (this.debug) {
      console.log('[KeyboardSequence] Initialized with sequences:',
        this.sequences.map(s => ({ sequence: s.sequence, description: s.description }))
      );
    }
  }

  private onKeyDown(e: KeyboardEvent): void {
    // Ignore if user is typing in an input field (unless they want to detect it)
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // Only add printable characters (length === 1)
    if (e.key.length === 1) {
      const key = this.caseSensitive ? e.key : e.key.toLowerCase();
      this.buffer += key;

      // Keep buffer at max length
      if (this.buffer.length > this.maxLength) {
        this.buffer = this.buffer.slice(-this.maxLength);
      }

      if (this.debug) {
        console.log('[KeyboardSequence] Current buffer:', this.buffer);
      }

      // Check all sequences
      this.checkSequences();
    } else if (this.resetOnNonMatch) {
      // Reset buffer on special keys (Enter, Escape, etc.)
      this.reset();
    }
  }

  private checkSequences(): void {
    for (const config of this.sequences) {
      const sequence = this.caseSensitive ? config.sequence : config.sequence.toLowerCase();

      if (this.buffer.endsWith(sequence)) {
        if (this.debug) {
          console.log(`[KeyboardSequence] Sequence matched: "${config.sequence}"${config.description ? ` (${config.description})` : ''}`);
        }

        config.callback();
        this.reset();
        break;
      }
    }
  }

  private reset(): void {
    this.buffer = '';
    if (this.debug) {
      console.log('[KeyboardSequence] Buffer reset');
    }
  }

  /**
   * Add a new sequence dynamically
   */
  public addSequence(config: SequenceConfig): void {
    this.sequences.push(config);
    this.maxLength = Math.max(this.maxLength, config.sequence.length);

    if (this.debug) {
      console.log('[KeyboardSequence] Added sequence:', config.sequence);
    }
  }

  /**
   * Remove a sequence by its string
   */
  public removeSequence(sequence: string): void {
    this.sequences = this.sequences.filter(s => s.sequence !== sequence);
    this.maxLength = this.sequences.length > 0
      ? Math.max(...this.sequences.map(s => s.sequence.length))
      : 0;

    if (this.debug) {
      console.log('[KeyboardSequence] Removed sequence:', sequence);
    }
  }

  /**
   * Clean up event listeners
   */
  public destroy(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    this.buffer = '';

    if (this.debug) {
      console.log('[KeyboardSequence] Destroyed');
    }
  }

  /**
   * Get current buffer (useful for debugging)
   */
  public getBuffer(): string {
    return this.buffer;
  }
}
